import { useEffect, useState } from "react";
import moment from "moment";
import useTranslator from "../../hooks/useTranslator";
import CheckIcon0 from "../../assets/img/checkIcon02.svg";
import {
  convertCurrencyFormat,
  getUpdatedExchangeIncome,
} from "../../helper/common";
import {
  getAvailablePayslipFields,
  getPayslipFieldValue,
  getPayslipFieldsFor,
} from "../../helper/payslipSelection";
import { ChoiceGroup } from "../design";
import IncomePanel, {
  IncomePanelDoc,
  IncomePanelLatest,
  IncomePanelMode,
  IncomePanelSource,
} from "./IncomePanel";

// translation keys of the payslip figures
const FIELD_LABEL_KEYS = { net: "netIncome", gross: "grossIncome" };

/**
 * Payslip side panel.
 * Step 2: payslips grouped by employer (tick to include, Latest tag,
 *         Details -> step 3, View -> document in a new tab).
 * Step 3: figures of one payslip (net OR gross, radio).
 * Back on step 3 returns to step 2; on step 2 it closes the panel.
 */
const PayslipSelectionModal = ({
  isOpen,
  onClose,
  employerGroups = [],
  selectedIds = [],
  fieldMap = {},
  latestIds = [],
  onTogglePayslip,
  onToggleField,
  total = 0,
  originalUser,
  getDocumentUrl,
  // retired tenant: entries are pensions, marked "(Pension)"
  isPension = false,
  // Sum / Mean of the selected payslips
  mode = "sum",
  onModeChange,
}) => {
  const { _t } = useTranslator();

  const [level, setLevel] = useState(2);
  const [activeId, setActiveId] = useState(null);

  // always start on the payslip list
  useEffect(() => {
    if (isOpen) {
      setLevel(2);
      setActiveId(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const selected = new Set(selectedIds);
  const latest = new Set(latestIds);
  const allItems = employerGroups.flatMap((g) => g.payslips);
  const activeItem =
    level === 3 ? (allItems.find((i) => i.id === activeId) ?? null) : null;

  const monthLabel = (item) =>
    item?.month ? moment(item.month, "YYYY-MM").format("MMM YYYY") : "";
  const title = (item) =>
    `${_t("payslip")} ${monthLabel(item)}${
      isPension ? ` (${_t("pension")})` : ""
    }`;
  const eur = (item, value) =>
    convertCurrencyFormat(
      getUpdatedExchangeIncome(value ?? 0, item?.currency, originalUser) || 0
    );
  const converted = (item) =>
    item?.currency && item.currency !== "EUR" ? ` (${_t("converted")})` : "";

  const backToList = () => {
    setLevel(2);
    setActiveId(null);
  };

  const renderList = () => (
    <>
      {employerGroups.map(({ employer, payslips }) => (
        <div key={employer} className="incomePanel__group">
          <h3 className="incomePanel__heading">{employer || _t("payslip")}</h3>
          <div className="incomePanel__list">
            {payslips.map((item) => {
              const fields = getPayslipFieldsFor(item, fieldMap);
              const amount = fields.reduce(
                (sum, f) => sum + (Number(getPayslipFieldValue(item, f)) || 0),
                0
              );
              return (
                <IncomePanelDoc
                  key={item.id}
                  isOn={selected.has(item.id)}
                  title={title(item)}
                  badges={
                    <>
                      {latest.has(item.id) && <IncomePanelLatest />}
                      {item?.verified && (
                        <img
                          src={CheckIcon0}
                          alt="Verified"
                          className="incomePanel__verified"
                        />
                      )}
                    </>
                  }
                  meta={`${fields.map((f) => _t(f)).join(" + ")}: €${eur(
                    item,
                    amount
                  )}${converted(item)}`}
                  onToggle={() => onTogglePayslip(item.id)}
                  onDetails={() => {
                    setActiveId(item.id);
                    setLevel(3);
                  }}
                  viewUrl={getDocumentUrl?.(item)}
                />
              );
            })}
          </div>
        </div>
      ))}
    </>
  );

  const renderFields = () => {
    const chosen = getPayslipFieldsFor(activeItem, fieldMap);
    const isSelected = selected.has(activeItem.id);
    return (
      <>
        <IncomePanelSource
          title={title(activeItem)}
          viewUrl={getDocumentUrl?.(activeItem)}
        />
        <ChoiceGroup
          name="payslipField"
          layout="list"
          value={
            getAvailablePayslipFields(activeItem).find(
              (f) => isSelected && chosen.includes(f)
            ) ?? ""
          }
          onChange={(e) => onToggleField(activeItem, e.target.value)}
          options={getAvailablePayslipFields(activeItem).map((f) => ({
            value: f,
            label: _t(FIELD_LABEL_KEYS[f] ?? f),
            aside: `€${eur(
              activeItem,
              getPayslipFieldValue(activeItem, f)
            )}${converted(activeItem)}`,
          }))}
        />
      </>
    );
  };

  return (
    <IncomePanel
      isOpen={isOpen}
      onClose={onClose}
      onBack={activeItem ? backToList : onClose}
      title={activeItem ? title(activeItem) : _t("payslips")}
      crumbs={[
        { label: _t("income"), onClick: onClose },
        { label: _t("payslips"), onClick: backToList },
        ...(activeItem ? [{ label: title(activeItem) }] : []),
      ]}
      total={total}
      actionLabel={activeItem ? _t("back") : _t("done")}
      onAction={activeItem ? backToList : onClose}
      footerExtra={
        activeItem ? null : (
          <IncomePanelMode
            mode={mode}
            onChange={onModeChange}
            count={selectedIds.length}
          />
        )
      }
    >
      {activeItem ? renderFields() : renderList()}
    </IncomePanel>
  );
};

export default PayslipSelectionModal;
